import { useState, useRef } from "react";
import { useHistory } from "react-router-dom";

import {
	API_URL,
	AUTHNET_SCRIPT,
	AUTHNET_LOGINID,
	AUTHNET_PUBKEY,
	isCardType,
	getValFromLS,
} from "../helpers";
import { useCreateScriptTag } from "../use-createscripttag-hook";

import Input from "../components/Input";
import Msg from "../components/Msg";
import Submit from "../components/Submit";
import CardTypes from "../components/CardTypes";

const AuthnetForm = ({ order, other }) => {
	const [errors, setErrors] = useState([]);
	const [names, setNames] = useState({
		other,
		number: "",
	});
	const [msg, setMsg] = useState({});

	const formElement = useRef();
	const history = useHistory();
	useCreateScriptTag(AUTHNET_SCRIPT);

	const handleUpdate = (name, value) => {
		if (name === "number") {
			const cardType = isCardType(value);
			setNames((names) => ({ ...names, [name]: value, cardType }));
		} else if (name === "other") {
			setNames((names) => ({
				...names,
				other: parseFloat(value.replaceAll(",", "")),
			}));
		} else {
			setNames((names) => ({ ...names, [name]: value ? value : "" }));
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setErrors([]);
		setMsg({
			type: "working",
			text: "",
		});

		if (!names.other) {
			setMsg({
				type: "error",
				text: "Please enter a valid payment amount.",
			});
			setErrors(["other"]);

			return;
		}

		let newErrors = [];
		["number", "expdate", "code", "cardholder", "zip"].forEach((name) => {
			if (!names[name]) {
				newErrors.push(name);
			}
		});

		if (newErrors.length > 0) {
			setMsg({
				type: "error",
				text: "Please make an entry in all required fields (*).",
			});
			setErrors(newErrors);

			return;
		}

		if (isCardType(names.number) === "no") {
			setMsg({
				type: "error",
				text: "Card number entered not valid or type not supported.",
			});
			setErrors(["number"]);

			return;
		}

		if (!names.expdate.match(/^([0-9]{4})$/)) {
			setMsg({
				type: "error",
				text: "Please check the syntax of your expiration date (mmyy).",
			});
			setErrors(["expdate"]);

			return;
		}

		const authnetData = {
			apiLoginID: AUTHNET_LOGINID,
			clientKey: AUTHNET_PUBKEY,
		};
		const authnetCard = {
			cardNumber: names.number.replace(/\s/g, "").replace(/-/g, ""),
			month: names.expdate.substring(0, 2),
			year: names.expdate.substring(2, 4),
			cardCode: names.code,
			fullName: names.cardholder,
			zip: names.zip,
		};
		window.Accept.dispatchData(
			{ authData: authnetData, cardData: authnetCard },
			handleResponse
		);
	};

	const handleResponse = (response) => {
		if (response.messages.resultCode === "Error") {
			//console.log(response.messages.message);
			setMsg({
				type: "error",
				text: `An error has occurred, (${response.messages.message[0].code}) ${response.messages.message[0].text}`,
			});
		} else {
			//console.log(response.opaqueData);
			handleSuccess(response.opaqueData);
		}
	};

	const handleSuccess = async (opaqueData) => {
		const url = new URL(`${API_URL}/dashboardPaymentAuthnet`);
		const formData = new FormData();
		formData.append("other", names.other);
		formData.append("opaqueData", JSON.stringify(opaqueData) || "{}");
		formData.append("cardholder", names.cardholder);
		formData.append("zip", names.zip);
		formData.append("order", JSON.stringify(order));
		formData.append("auth", getValFromLS("dash", true));

		try {
			const response = await fetch(url, {
				method: "POST",
				body: formData,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				history.push("/dashboard/receipt", {
					replace: true,
					message: {
						type: "success",
						text: json.text,
					},
				});
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			console.log(error);

			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	return (
		<form
			ref={formElement}
			method="post"
			action="/"
			onSubmit={(e) => handleSubmit(e)}
			className="well"
			style={{ marginTop: "25px" }}
		>
			<h3 style={{ marginTop: "0" }}>Pay Invoice by Credit / Debit Card</h3>

			<div className="form-group" style={{ marginBottom: "20px" }}>
				<Input
					name="other"
					label="Enter Payment Amount"
					reqd={true}
					value={names.other || ""}
					update={handleUpdate}
					errors={errors}
					addonIcon="fa-dollar-sign"
				/>
				<div className="empty" />
			</div>

			<CardTypes type={isCardType(names.number)} />

			<div className="form-group three">
				<Input
					type="text-secure"
					name="number"
					label="Credit / Debit Card Number"
					reqd={true}
					value={""}
					update={handleUpdate}
					errors={errors}
					autocomplete="cc-number"
				/>

				<Input
					type="text-secure"
					name="expdate"
					label="Expiration Date (mmyy)"
					reqd={true}
					value={""}
					update={handleUpdate}
					errors={errors}
					placeholder="mmyy"
					autocomplete="cc-exp"
				/>

				<Input
					type="text-secure"
					name="code"
					label="CSC/CVV Code"
					reqd={true}
					value={""}
					update={handleUpdate}
					errors={errors}
					autocomplete="cc-csc"
				/>
			</div>

			<div className="form-group">
				<Input
					type="text-secure"
					name="cardholder"
					label="Cardholder Name"
					reqd={true}
					value={""}
					update={handleUpdate}
					errors={errors}
					autocomplete="name"
				/>

				<Input
					type="text-secure"
					name="zip"
					label="Billing Zip Code"
					reqd={true}
					value={""}
					update={handleUpdate}
					errors={errors}
					autocomplete="postal-code"
				/>
			</div>

			<div className="terms" style={{ marginTop: "20px", height: "135px" }}>
				<p className="bold" style={{ fontSize: "1.1rem" }}>
					Verify your payment details. Once you click Submit Payment, your
					credit card will be charged.
				</p>
				<p>
					<span>
						All credit card charges will show up under the name{" "}
						<span className="bold">Benefit for Children</span>. All transactions
						are final. Once the transaction is completed successfully, your
						outstanding balance will be credited for the amount paid.
					</span>
				</p>
			</div>

			<div className="msg-submit">
				{msg.type && <Msg data={msg} />}

				<div
					className={
						msg.type === "working" || msg.type === "success"
							? "hidden"
							: "submit-container"
					}
				>
					<Submit submit={"Submit Payment"} />
				</div>
			</div>
		</form>
	);
};

export default AuthnetForm;
